import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Swal from 'sweetalert2'
import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'

import { IPostVodafoneServices } from '../models/models'
import { GetAuthorizedServicesVodafoneApi } from '../services/getAuthorizedServicesVodafoneApi'
import { DeleteAuthorizedServicesVodafoneApi } from '../services/deleteAuthorizedServicesVodafoneApi'
import { PostAuthorizedServicesVodafoneApi } from '../services/postAuthorizedServicesVodafoneApi'
import { PutAuthorizedServicesVodafoneApi } from '../services/putAuthorizedServicesVodafoneApi'

type Row = IPostVodafoneServices & { _tempId: string }

function AddAuthorizedServicesVodafone(props: BaseComponentWithContextProps) {
  const [rows, setRows] = useState<Row[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const makeTempId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

  const fetchRows = async () => {
    try {
      setIsLoading(true)
      props.showLoading?.()

      const api = new GetAuthorizedServicesVodafoneApi({})
      const data = await api.getAuthorizedServicesMain()

      const mapped: Row[] = (data || []).map(item => ({
        Id: Number(item.Id ?? 0),
        Title: item.Title ?? '',
        Link: item.Link ?? '',
        _tempId: makeTempId(),
      }))

      setRows(mapped)
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Kayıtlar getirilemedi',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      setIsLoading(false)
      props.hideLoading?.()
    }
  }

  useEffect(() => {
    fetchRows()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const addRow = () => {
    setRows(prev => [
      ...prev,
      {
        Id: 0,
        Title: '',
        Link: '',
        _tempId: makeTempId(),
      },
    ])
  }

  const updateRow = (tempId: string, field: 'Title' | 'Link', value: string) => {
    setRows(prev => prev.map(r => (r._tempId === tempId ? { ...r, [field]: value } : r)))
  }

  const saveRow = async (row: Row) => {
    const title = row.Title?.trim()
    const link = row.Link?.trim()

    if (!title || !link) {
      await Swal.fire({
        icon: 'error',
        title: 'Başlık ve Link zorunludur',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const dto: IPostVodafoneServices = {
      Id: Number(row.Id ?? 0),
      Title: title,
      Link: link,
    }

    try {
      props.showLoading?.()

      if (!dto.Id || dto.Id === 0) {
        // INSERT: tek kayıt
        const api = new PostAuthorizedServicesVodafoneApi({})
        const created = await api.postAuthorizedServicesMain(dto)

        // DB Id döndüyse UI satırına yaz
        if (created?.Id) {
          setRows(prev => prev.map(r => (r._tempId === row._tempId ? { ...r, Id: created.Id } : r)))
        }

        Swal.fire({ icon: 'success', title: 'Kaydedildi', showConfirmButton: true, allowOutsideClick: false })
      } else {
        // UPDATE: tek kayıt
        const api = new PutAuthorizedServicesVodafoneApi({})
        await api.putAuthorizedServicesMain(dto)

        Swal.fire({ icon: 'success', title: 'Güncellendi', showConfirmButton: true, allowOutsideClick: false })
      }
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Kaydetme başarısız',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading?.()
    }
  }

  const deleteRow = async (row: Row) => {
    // DB kaydı yoksa sadece UI’dan kaldır
    if (!row.Id || row.Id === 0) {
      setRows(prev => prev.filter(r => r._tempId !== row._tempId))
      return
    }

    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Silmek istediğinize emin misiniz?',
      showCancelButton: true,
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'Vazgeç',
      allowOutsideClick: false,
    })

    if (!confirm.isConfirmed) return

    try {
      props.showLoading?.()

      const api = new DeleteAuthorizedServicesVodafoneApi({})
      await api.deleteVodafoneService(row.Id)

      setRows(prev => prev.filter(r => r._tempId !== row._tempId))

      Swal.fire({
        icon: 'success',
        title: 'Kayıt silindi',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } catch (err: any) {
      Swal.fire({
        icon: 'error',
        title: err?.description || 'Silme işlemi başarısız',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    } finally {
      props.hideLoading?.()
    }
  }

  return (
    <div className="container">
      <div className="content col-lg-12 m-auto">
        <div className="card">
          <div className="card-header d-flex align-items-center">
            <span className="h4 mb-0">Yetkili Servisler</span>

            <div className="d-flex gap-2 ms-auto me-3">
              <IPButton type="button" className="btn btn-primary" text="Ekle" onClick={addRow} />

              <Link to="/admin/BrandPageOperation/AuthorizedServices/ListAuthorizedServices">
                <IPButton type="button" className="btn btn-primary" text="Listeye Dön" />
              </Link>
            </div>
          </div>

          <div className="card-body">
            {isLoading && rows.length === 0 ? (
              <div>Yükleniyor...</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: '35%' }}>Başlık</th>
                      <th>Link</th>
                      <th style={{ width: '170px' }} className="text-end">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={3}>Kayıt bulunamadı.</td>
                      </tr>
                    ) : (
                      rows.map(row => (
                        <tr key={row._tempId}>
                          <td>
                            <IPInput
                              type="text"
                              className="form-control"
                              name={`Title-${row._tempId}`}
                              value={row.Title}
                              onChange={(e: any) => updateRow(row._tempId, 'Title', e.target.value)}
                            />
                          </td>
                          <td>
                            <IPInput
                              type="text"
                              className="form-control"
                              name={`Link-${row._tempId}`}
                              value={row.Link}
                              onChange={(e: any) => updateRow(row._tempId, 'Link', e.target.value)}
                            />
                          </td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <IPButton
                                type="button"
                                className="btn btn-success"
                                text={row.Id && row.Id > 0 ? 'Güncelle' : 'Kaydet'}
                                onClick={() => saveRow(row)}
                              />
                              <IPButton
                                type="button"
                                className="btn btn-danger"
                                text="Sil"
                                onClick={() => deleteRow(row)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(AddAuthorizedServicesVodafone)
